import { useDialog } from '@/common/dialog/DialogContext';
import { EntityList } from '@/common/entityFramework/entityList/EntityList';
import { useNavState } from '@/common/navState/NavStateContext';
import { usePrincipal } from '@/common/principal/PrincipalContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Title } from '@/common/title/Title';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { ObjUtils } from '@/common/utils/ObjUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { Box } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { IncListCompareDialog } from '../pol/compare/IncListCompareDialog';

export const IncRuleTreeList = ({ incDataSrcId }) => {
  const { navState } = useNavState();
  const { hasRole } = usePrincipal();
  const { openDialog } = useDialog();
  const rsrc = useRsrc();

  const [incDataSrcs, setIncDataSrcs] = useState([]);

  const incDataSrcIds = useMemo(() => {
    const fromPropOrNav = incDataSrcId ?? navState?.incDataSrcId;
    if (Array.isArray(fromPropOrNav)) {
      return fromPropOrNav.filter(Boolean);
    }
    if (fromPropOrNav) {
      return [fromPropOrNav];
    }
    return [];
  }, [incDataSrcId, navState?.incDataSrcId]);

  const isCompare = incDataSrcIds.length === 2;
  const incDataSrcKey = useMemo(() => incDataSrcIds.join('|'), [incDataSrcIds]);

  useEffect(() => {
    if (incDataSrcIds.length === 0) {
      setIncDataSrcs([]);
      return;
    }

    rsrc.rows('gp', 'INC_DATA_SRC', { expr: ExprUtils.whereParams('id', incDataSrcIds) }).then((rows) => {
      const ordered = new Array(incDataSrcIds.length);
      rows.forEach((row) => {
        const index = incDataSrcIds.indexOf(row.id);
        if (index >= 0) {
          ordered[index] = row;
        }
      });
      setIncDataSrcs(ordered.filter(Boolean));
    });
  }, [incDataSrcKey]);

  const augFieldDefs = useCallback(
    (fieldToFieldDef) => {
      ObjUtils.clearObj(fieldToFieldDef);

      fieldToFieldDef.code = { field: 'code', autoGrouping: true };
      fieldToFieldDef.name = { field: 'name' };
      fieldToFieldDef.entity = { field: 'entity', maxWidth: 150 };

      if (!isCompare) {
        fieldToFieldDef.inclusion = { field: 'inclusion', displayName: 'Inclusionary', type: 'BOOLEAN', maxWidth: 150 };
        fieldToFieldDef.trxCol = { field: 'trxCol', displayName: 'Transaction Column' };
        fieldToFieldDef.operator = { field: 'operator', minWidth: 205 };
        fieldToFieldDef.value = {
          field: 'value',
          minWidth: 205,
          flex: 1,
          wrapText: false,
          autoHeight: false,
        };
        return;
      }

      const [leftSrc, rightSrc] = incDataSrcs;
      fieldToFieldDef.match = {
        field: 'match',
        width: 70,
        cellClass: 'ag-center-cell',
        type: 'MATCH',
      };

      fieldToFieldDef.left = {
        field: 'left',
        displayName: (leftSrc?.name ?? 'Left Data Source') + ' Values',
        children: [
          { displayName: 'Inclusionary', field: 'inclusion0', type: 'BOOLEAN', maxWidth: 150 },
          { displayName: 'Transaction Column', field: 'trxCol0' },
          { displayName: 'Operator', field: 'operator0', minWidth: 205 },
          { displayName: 'Value', field: 'value0', minWidth: 250, flex: 2, wrapText: true, autoHeight: true },
        ],
      };

      fieldToFieldDef.right = {
        field: 'right',
        displayName: (rightSrc?.name ?? 'Right Data Source') + ' Values',
        children: [
          { displayName: 'Inclusionary', field: 'inclusion1', type: 'BOOLEAN', maxWidth: 150 },
          { displayName: 'Transaction Column', field: 'trxCol1' },
          { displayName: 'Operator', field: 'operator1', minWidth: 205 },
          { displayName: 'Value', field: 'value1', minWidth: 250, flex: 2, wrapText: true, autoHeight: true },
        ],
      };
    },
    [isCompare, incDataSrcs],
  );

  const clientSideRowData = useCallback(
    (setDataSource) => {
      if (incDataSrcIds.length === 0) {
        setDataSource([]);
        return;
      }

      const reqParam = isCompare ? { incDataSrcIds } : { incDataSrcId: incDataSrcIds[0] };

      rsrc.data('gp', 'INC_RULE_DATA_PLUGIN', reqParam).then((res) => {
        setDataSource(res);
      });
    },
    [incDataSrcKey, isCompare],
  );

  const openIncListCompareDialog = (rowData) => {
    if (incDataSrcs.length !== 2) {
      return;
    }

    const data = {
      title0: 'List from: ' + incDataSrcs[0].name,
      title1: 'List from: ' + incDataSrcs[1].name,
      value0: rowData.value0,
      value1: rowData.value1,
    };
    openDialog(IncListCompareDialog, { data });
  };

  const getContextMenuItems = (params) => {
    if (!isCompare) {
      return [];
    }

    const data = params.node.data;
    const listOperators = ['Is One Of', 'Is Not One Of'];
    const incListApplicable = listOperators.includes(data.operator0) || listOperators.includes(data.operator1);
    const hasIncListView = hasRole('GP_INC_LIST_VIEW');
    if (!incListApplicable || !hasIncListView) {
      return [];
    }

    return [
      {
        name: 'Compare Inclusion Lists',
        icon: fas.faSignInAlt,
        action: () => openIncListCompareDialog(data),
      },
    ];
  };

  const title = 'Inclusion Rules for ' + incDataSrcs.map((src) => src.name ?? 'Unknown').join(' vs. ');

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between', width: '100%' }}
    >
      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <Title icon={fas.faBalanceScale} title={title} />
      </Box>
      <EntityList
        module="gp"
        entity="INC_RULE"
        showTitle={false}
        augFieldDefs={augFieldDefs}
        clientSideRowData={clientSideRowData}
        gridOptions={{
          treeData: true,
          defaultExpandLevel: 1,
          getContextMenuItems,
        }}
      />
    </Box>
  );
};
